import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Form, Select, DatePicker, Input, Button, Typography, Space } from 'antd';
import { ArrowLeftOutlined, AlertOutlined, SendOutlined } from '@ant-design/icons';
import { toast } from 'react-toastify';
import { clientService } from '../../../common/services';
import Loader from '../../../components/loader/Loader';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const typesSinistre = [
    'Accident de circulation',
    'Vol',
    'Incendie',
    'Bris de glace',
    'Degat des eaux',
    'Catastrophe naturelle',
    'Responsabilite civile',
    'Maladie / Hospitalisation',
    'Dommage aux cultures',
    'Autre',
];

export default function DeclarerSinistre() {
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const [contrats, setContrats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        loadContrats();
    }, []);

    const loadContrats = async () => {
        try {
            const resp = await clientService.getContrats();
            if (resp.success) {
                // Filtrer les contrats actifs
                setContrats(resp.data.filter(c => c.statut === 'actif'));
            }
        } catch (error) {
            toast.error('Erreur lors du chargement des contrats');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            const data = {
                contrat: values.contrat,
                dateSinistre: values.dateSinistre.toISOString(),
                typeSinistre: values.typeSinistre,
                description: values.description,
            };

            const resp = await clientService.declarerSinistre(data);
            if (resp.success) {
                toast.success('Votre sinistre a ete declare avec succes. Vous serez contacte rapidement.');
                navigate('/espace-client/sinistres');
            } else {
                toast.error(resp.message || 'Erreur lors de la declaration');
            }
        } catch (error) {
            toast.error(error.message || 'Erreur lors de la declaration du sinistre');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <div>
            <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/espace-client/sinistres')}
                style={{ padding: 0, marginBottom: 16 }}
            >
                Retour a mes sinistres
            </Button>

            <Title level={3}>
                <AlertOutlined style={{ marginRight: 8 }} />
                Declarer un sinistre
            </Title>

            <Card style={{ maxWidth: 700 }}>
                <Paragraph type="secondary" style={{ marginBottom: 24 }}>
                    Remplissez ce formulaire pour declarer un sinistre sur l'un de vos contrats actifs.
                    Notre equipe vous contactera dans les plus brefs delais.
                </Paragraph>

                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    size="large"
                >
                    <Form.Item
                        name="contrat"
                        label="Contrat concerne"
                        rules={[{ required: true, message: 'Veuillez selectionner un contrat' }]}
                    >
                        <Select placeholder="Selectionnez le contrat">
                            {contrats.map(c => (
                                <Select.Option key={c._id} value={c._id}>
                                    {c.numeroPolice} - {c.typeAssurance?.nom} ({c.compagnie?.nom})
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="dateSinistre"
                        label="Date du sinistre"
                        rules={[{ required: true, message: 'Veuillez indiquer la date du sinistre' }]}
                    >
                        <DatePicker
                            style={{ width: '100%' }}
                            format="DD/MM/YYYY"
                            placeholder="Selectionnez la date"
                        />
                    </Form.Item>

                    <Form.Item
                        name="typeSinistre"
                        label="Type de sinistre"
                        rules={[{ required: true, message: 'Veuillez selectionner le type de sinistre' }]}
                    >
                        <Select placeholder="Selectionnez le type">
                            {typesSinistre.map(t => (
                                <Select.Option key={t} value={t}>{t}</Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description des faits"
                        rules={[{ required: true, message: 'Veuillez decrire les circonstances du sinistre' }]}
                    >
                        <TextArea
                            rows={5}
                            placeholder="Decrivez les circonstances du sinistre : lieu, heure, deroulement des faits, dommages constates..."
                        />
                    </Form.Item>

                    <Space>
                        <Button onClick={() => navigate('/espace-client/sinistres')}>
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={submitting}
                            icon={<SendOutlined />}
                        >
                            Declarer le sinistre
                        </Button>
                    </Space>
                </Form>
            </Card>
        </div>
    );
}
